/**
 * Workspace Context for Enterprise Multi-Tenant Architecture
 * ==========================================================
 * 
 * Provides workspace detection and context throughout the React app.
 * 
 * Usage:
 *   const { workspace, isMainApp, isLoading } = useWorkspace();
 *   
 * Features:
 *   - Detects subdomain from current URL
 *   - Fetches workspace info from API
 *   - Provides workspace branding (name, colors, logo)
 *   - Handles workspace not found / suspended states
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAIN_DOMAIN = 'lexa.run';
const MAIN_SUBDOMAINS = ['app', 'www', ''];

const WorkspaceContext = createContext(null);

/**
 * Extract subdomain from current hostname
 */
function getSubdomainFromHost() {
  const hostname = window.location.hostname.toLowerCase();
  
  // Development environments - no workspace
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  
  // Preview/staging environments (Emergent)
  if (hostname.includes('.stage-preview.emergentagent.com') || hostname.includes('.preview.emergentagent.com')) {
    return null;
  }
  
  // QA environment (preview.qa.lexa.run)
  if (hostname.includes('.qa.lexa.run') || hostname === 'qa.lexa.run') {
    return null;
  }
  
  // Check if it's our main domain
  if (!hostname.endsWith(`.${MAIN_DOMAIN}`) && hostname !== MAIN_DOMAIN) {
    // Custom domain or unknown - treat as main app for now
    return null;
  }
  
  // Bare domain
  if (hostname === MAIN_DOMAIN) {
    return null;
  }
  
  // Extract subdomain: ruonn.lexa.run -> ruonn
  const subdomain = hostname.replace(`.${MAIN_DOMAIN}`, '');
  
  // Reserved subdomains go to main app
  if (MAIN_SUBDOMAINS.includes(subdomain)) {
    return null;
  }
  
  return subdomain;
}

/**
 * Build workspace URL from subdomain
 */
export function buildWorkspaceUrl(subdomain) {
  if (!subdomain) return `https://app.${MAIN_DOMAIN}`;
  return `https://${subdomain}.${MAIN_DOMAIN}`;
}

/**
 * Workspace Provider Component
 */
export function WorkspaceProvider({ children }) {
  const [workspace, setWorkspace] = useState(null);
  const [subdomain, setSubdomain] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMainApp, setIsMainApp] = useState(true);

  useEffect(() => {
    const fetchWorkspace = async () => {
      const detectedSubdomain = getSubdomainFromHost();
      setSubdomain(detectedSubdomain);
      
      if (!detectedSubdomain) {
        // Main app - no workspace context needed
        setIsMainApp(true);
        setIsLoading(false);
        return;
      }
      
      setIsMainApp(false);
      
      try {
        // Fetch workspace info from API
        const response = await axios.get(
          `${BACKEND_URL}/api/workspaces/by-subdomain/${detectedSubdomain}`
        );
        
        setWorkspace(response.data);
        setError(null);
        
        // Apply workspace branding to CSS variables
        if (response.data?.settings?.primary_color) {
          document.documentElement.style.setProperty(
            '--workspace-primary-color',
            response.data.settings.primary_color
          );
        }
      } catch (err) {
        console.error('Failed to fetch workspace:', err);
        if (err.response?.status === 404) {
          setError('workspace_not_found');
        } else if (err.response?.status === 403) {
          setError('workspace_suspended');
        } else {
          setError('fetch_failed');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkspace();
  }, []);

  const value = {
    workspace,
    subdomain,
    isLoading,
    error,
    isMainApp,
    workspaceUrl: subdomain ? buildWorkspaceUrl(subdomain) : null,
    mainAppUrl: buildWorkspaceUrl(null),
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

/**
 * Hook to access workspace context
 */
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

/**
 * Workspace Not Found Component
 */
export function WorkspaceNotFound({ subdomain }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Workspace Not Found</h1>
          <p className="text-gray-600 mb-6">
            The workspace <span className="font-mono font-medium">{subdomain}.lexa.run</span> doesn't exist or has been removed.
          </p>
        </div>
        <div className="space-y-3">
          <a
            href={`https://app.${MAIN_DOMAIN}`}
            className="block w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
          >
            Go to Main Site
          </a>
          <a
            href={`https://app.${MAIN_DOMAIN}`}
            className="block w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors"
          >
            Create a New Workspace
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Workspace Suspended Component
 */
export function WorkspaceSuspended({ workspace }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Workspace Suspended</h1>
          <p className="text-gray-600 mb-6">
            This workspace has been temporarily suspended. Please contact your administrator or support for assistance.
          </p>
        </div>
        <a
          href="mailto:support@lexa.run"
          className="inline-block py-3 px-6 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
        >
          Contact Support
        </a>
      </div>
    </div>
  );
}

/**
 * Workspace Loading Component
 */
export function WorkspaceLoading() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Loading workspace...</p>
      </div>
    </div>
  );
}

export default WorkspaceContext;
